import type { EmojiCache } from "./emojiExportUtils";
import { replaceTextWithEmojiImages } from "./emojiExportUtils";

/**
 * Нормализует шрифт текстового элемента для экспорта:
 */
function normalizeFontFamily(textElement: SVGTextElement): void {
    const font = textElement.getAttribute("font-family");
    if (font === "serif") {
        // В некоторых шаблонах используется serif. Ближе к нему Times New Roman
        textElement.setAttribute("font-family", "Times New Roman");
    } else {
        // Остальной текст на доске обычно sans-serif. Ближе к нему Arial
        textElement.setAttribute("font-family", "Arial");
    }
}

/**
 * Подготавливает текстовые элементы SVG для экспорта:
 * - Нормализует шрифты текстовых элементов
 * - Заменяет текстовые эмодзи на изображения
 */
export function prepareTextForExport(svgElement: HTMLElement, cache?: EmojiCache): void {
    // Преобразуем NodeList в массив, так как некоторые элементы могут быть заменены на группы
    const textElements = Array.from(svgElement.querySelectorAll("text"));

    textElements.forEach((textElement) => {
        normalizeFontFamily(textElement as SVGTextElement);

        const group = replaceTextWithEmojiImages(textElement as SVGTextElement, cache);
        if (group && textElement.parentNode) {
            textElement.parentNode.replaceChild(group, textElement);
        }
    });
}

/**
 * Исправляет проблему с невидимым текстом в стрелках при экспорте в PDF.
 * Проблема: текст есть в PDF (можно скопировать), но невидимый из-за того,
 * что clip-path обрезает текст так, что он становится невидимым в jsPDF.
 *
 * Решение: перемещаем текст из группы с clip-path в отдельную группу без clip-path,
 * если текст находится в той же группе, что и стрелка (path/line).
 * Clip-path для стрелки сохраняется, чтобы стрелка не перечеркивала текст.
 */
function fixArrowTextVisibility(clipPathGroups: NodeListOf<Element>): void {
    // Фильтруем группы, которые содержат и текст, и стрелку одновременно
    const groupsToProcess = Array.from(clipPathGroups).filter((g) => {
        // Проверяем наличие стрелки и текста одновременно
        const hasPathOrLine = !!g.querySelector("path, line");
        const hasText = !!g.querySelector("text");
        return hasPathOrLine && hasText;
    });

    // Обрабатываем только группы, которые содержат и текст, и стрелку одновременно
    for (const groupToProcess of groupsToProcess) {
        const textElements = Array.from(groupToProcess.querySelectorAll("text"));
        const parent = groupToProcess.parentElement;
        if (!parent) {
            continue;
        }

        const textGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");

        // Копируем общие атрибуты из исходной группы, чтобы избежать дублирования
        // на текстовых элементах (font-family, font-size и т.д.)
        const commonAttributes = ["font-family", "font-size", "font-weight"];
        for (const attr of commonAttributes) {
            const value = groupToProcess.getAttribute(attr);
            if (value) {
                textGroup.setAttribute(attr, value);
            }
        }

        for (const textEl of textElements) {
            const textElement = textEl as SVGTextElement;
            // Убеждаемся, что у текста есть fill атрибут
            if (!textElement.getAttribute("fill") || textElement.getAttribute("fill") === "none") {
                textElement.setAttribute("fill", "#2d2d2d");
            }
            // Перемещаем элемент напрямую, без клонирования, чтобы избежать дублирования
            textGroup.appendChild(textElement);
        }

        // Вставляем группу с текстом перед группой со стрелкой
        parent.insertBefore(textGroup, groupToProcess);
    }
}

/**
 * Подготавливает SVG элемент для экспорта в PDF:
 * - Исправляет проблему с clip-path для текста в стрелках
 * - Нормализует шрифты текстовых элементов и заменяет эмодзи на изображения
 */
export function prepareSvgForExport(
    svgElement: HTMLElement,
    emojiCacheFromParams?: EmojiCache,
): void {
    const cache = emojiCacheFromParams ?? new Map<string, string>();

    try {
        const clipPathGroups = svgElement.querySelectorAll("g[clip-path]");
        if (clipPathGroups.length > 0) {
            fixArrowTextVisibility(clipPathGroups);
        }

        prepareTextForExport(svgElement, cache);
    } finally {
        if (!emojiCacheFromParams) {
            cache.clear();
        }
    }
}
